/**
 * EpcScheduleMemberAssignView — Member-level task assignment studio.
 *
 *  • Department head: re-assigns tasks within their department to one of their managers.
 *  • Manager:         re-assigns tasks delegated to them to one of their engineers
 *                      (or unassigns to keep on themselves).
 *
 *  Drop-in: same shape as EpcScheduleDepartmentAssignView (tasks/onChange/onSave/dirty/saving).
 */

import { useCallback, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Building2,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  Loader2,
  Save,
  Target,
  User,
  UserCheck,
  Users,
  X,
  Zap,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { twMerge } from 'tailwind-merge';
import type {
  EpcProjectMembershipRow,
  EpcProjectOrgUnit,
} from '../../../services/epcProjectService';
import type { EpcScheduleTaskRow } from '../types/scheduleTask';
import {
  buildWbsTree,
  countWbsKinds,
  nodeSubtreeIndices,
  type EpcWbsNode,
} from './utils/epcScheduleWbsTree';
import { membershipDisplayName, taskAssignedUserId, taskOrgUnitId, orgUnitName } from './utils/epcScheduleDepartmentAssignment';
import {
  applyAssigneeToTaskIndices,
  authoritativeTaskIndices,
  computeStats,
  computeWorkload,
  eligibleAssignees,
  resolveMemberAssignRole,
  type MemberAssignRole,
} from './utils/epcScheduleMemberAssignment';
import {
  formatOverlapRange,
  predictAssignmentConflicts,
} from './utils/epcScheduleDateConflicts';
import { ROLE_LABEL } from '../project/team/teamCapabilities';

/* ─────────────────────────── PROPS ─────────────────────────── */

export type EpcScheduleMemberAssignViewProps = {
  tasks: EpcScheduleTaskRow[];
  onChange: (next: EpcScheduleTaskRow[]) => void;
  orgUnits: EpcProjectOrgUnit[];
  memberships: EpcProjectMembershipRow[];
  /** The current user's membership row. Required (parent guards on this). */
  mine: EpcProjectMembershipRow;
  myUserId: string;
  saving: boolean;
  dirty: boolean;
  onSave: () => void | Promise<void>;
  /** Whether the user can mutate (mirrors `canUpdate` from the panel). */
  canUpdate: boolean;
};

/* ─────────────────────────── ATOMS ─────────────────────────── */

function kindBadgeClasses(kind: EpcWbsNode['kind']): string {
  if (kind === 'stage') return 'bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200';
  if (kind === 'task') return 'bg-teal-100 text-teal-800 ring-1 ring-teal-200';
  return 'bg-slate-100 text-slate-600 ring-1 ring-slate-200';
}

function kindRailClasses(kind: EpcWbsNode['kind']): string {
  if (kind === 'stage') return 'border-l-[3px] border-l-indigo-500';
  if (kind === 'task') return 'border-l-[3px] border-l-teal-500';
  return 'border-l-[3px] border-l-slate-300';
}

function StatChip({
  label,
  value,
  tone = 'slate',
}: {
  label: string;
  value: string | number;
  tone?: 'slate' | 'teal' | 'amber' | 'emerald';
}) {
  const toneClass =
    tone === 'teal'
      ? 'bg-teal-50 text-teal-800 ring-teal-200'
      : tone === 'amber'
        ? 'bg-amber-50 text-amber-800 ring-amber-200'
        : tone === 'emerald'
          ? 'bg-emerald-50 text-emerald-800 ring-emerald-200'
          : 'bg-slate-50 text-slate-700 ring-slate-200';
  return (
    <div className={twMerge('rounded-lg px-3 py-2 ring-1', toneClass)}>
      <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</p>
      <p className="mt-0.5 text-[15px] font-bold tabular-nums">{value}</p>
    </div>
  );
}

/* ─────────────────────────── MAIN ─────────────────────────── */

export default function EpcScheduleMemberAssignView({
  tasks,
  onChange,
  orgUnits,
  memberships,
  mine,
  myUserId,
  saving,
  dirty,
  onSave,
  canUpdate,
}: EpcScheduleMemberAssignViewProps) {
  const role: MemberAssignRole | null = resolveMemberAssignRole(mine);

  /* ─── derive: in-scope task indices, WBS tree, eligible assignees, leaf set, workload ─── */
  const scopeIndices = useMemo<number[]>(
    () => (role ? authoritativeTaskIndices(tasks, role, mine, myUserId) : []),
    [tasks, role, mine, myUserId],
  );
  const scopeIndexSet = useMemo(() => new Set(scopeIndices), [scopeIndices]);

  /* Build a tree from the scoped subset only — keeps parents only when at least one descendant is in scope. */
  const scopedTasks = useMemo<EpcScheduleTaskRow[]>(
    () => scopeIndices.map((i) => tasks[i]!),
    [scopeIndices, tasks],
  );
  /** Map from "scoped tree row index" → original task index. */
  const scopedToOriginal = useMemo<number[]>(() => scopeIndices.slice(), [scopeIndices]);

  const tree = useMemo(() => buildWbsTree(scopedTasks), [scopedTasks]);
  const kinds = useMemo(() => countWbsKinds(tree), [tree]);

  /** Set of *scoped* indices that are leaves (no children in the scoped tree). */
  const leafScopedIndices = useMemo(() => {
    const s = new Set<number>();
    const walk = (n: EpcWbsNode) => {
      if (n.children.length === 0) s.add(n.rowIndex);
      else n.children.forEach(walk);
    };
    tree.forEach(walk);
    return s;
  }, [tree]);

  const isLeafOriginal = useCallback(
    (origIdx: number): boolean => {
      const scopedIdx = scopedToOriginal.indexOf(origIdx);
      return scopedIdx >= 0 && leafScopedIndices.has(scopedIdx);
    },
    [scopedToOriginal, leafScopedIndices],
  );

  const assignees = useMemo<EpcProjectMembershipRow[]>(
    () => (role ? eligibleAssignees(role, mine, myUserId, memberships) : []),
    [role, mine, myUserId, memberships],
  );

  const workload = useMemo(
    () => computeWorkload(tasks, scopeIndices, assignees),
    [tasks, scopeIndices, assignees],
  );

  const stats = useMemo(
    () => computeStats(tasks, scopeIndices, isLeafOriginal),
    [tasks, scopeIndices, isLeafOriginal],
  );

  /* ─── ui state ─── */
  const [expanded, setExpanded] = useState<Set<number>>(() => {
    /* Default: all stages expanded so the tree is actionable on first paint. */
    const s = new Set<number>();
    const walk = (n: EpcWbsNode) => {
      if (n.children.length > 0) s.add(n.rowIndex);
      n.children.forEach(walk);
    };
    tree.forEach(walk);
    return s;
  });
  const [selection, setSelection] = useState<Set<number>>(new Set()); // *scoped* indices
  const [bulkAssignee, setBulkAssignee] = useState<string>('');
  const [filterUnassignedOnly, setFilterUnassignedOnly] = useState(false);
  const [filterAssigneeId, setFilterAssigneeId] = useState<string>('');

  /* ─── handlers ─── */
  const toggleExpand = (rowIndex: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });

  const expandAll = () => {
    const s = new Set<number>();
    const walk = (n: EpcWbsNode) => {
      if (n.children.length > 0) s.add(n.rowIndex);
      n.children.forEach(walk);
    };
    tree.forEach(walk);
    setExpanded(s);
  };
  const collapseAll = () => setExpanded(new Set());

  const toggleSelection = (scopedIdx: number) =>
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(scopedIdx)) next.delete(scopedIdx);
      else next.add(scopedIdx);
      return next;
    });

  /** Select a node and all descendants in the scoped tree. */
  const selectSubtree = (node: EpcWbsNode) =>
    setSelection((prev) => {
      const next = new Set(prev);
      for (const i of nodeSubtreeIndices(node)) next.add(i);
      return next;
    });

  const clearSelection = () => setSelection(new Set());

  const setAssignee = useCallback(
    (scopedIndices: Iterable<number>, userId: string | undefined): boolean => {
      if (!canUpdate) return false;
      const originalIndices: number[] = [];
      for (const scoped of scopedIndices) {
        const orig = scopedToOriginal[scoped];
        if (orig != null) originalIndices.push(orig);
      }
      if (originalIndices.length === 0) return false;

      /* Hard rule: a member assigned to multiple departments cannot have two tasks
         on the same calendar day across different departments. We simulate the
         change first (so the org-unit-aware filter runs), then look for cross-dept
         overlaps. */
      if (userId) {
        const simulated = applyAssigneeToTaskIndices(tasks, originalIndices, userId);
        const stillAssigned = originalIndices.filter(
          (i) => taskAssignedUserId(simulated[i]!) === userId,
        );
        const conflicts = predictAssignmentConflicts(simulated, stillAssigned, userId);
        if (conflicts.length > 0) {
          const c = conflicts[0]!;
          const myDept = orgUnitName(orgUnits, c.candidateOrgId) || 'this dept';
          const otherDept = orgUnitName(orgUnits, c.existingOrgId) || 'another dept';
          const range = formatOverlapRange(c);
          toast.error(
            `Date conflict on ${range}: this member already has "${c.existingName}" in ${otherDept}. ` +
              `Cannot also take "${c.candidateName}" in ${myDept} on the same date.`,
            { duration: 6500 },
          );
          return false;
        }
      }

      const next = applyAssigneeToTaskIndices(tasks, originalIndices, userId);
      onChange(next);
      return true;
    },
    [canUpdate, scopedToOriginal, tasks, onChange, orgUnits],
  );

  const applyBulk = () => {
    if (selection.size === 0) {
      toast.error('Select at least one task first');
      return;
    }
    const ok = setAssignee(selection, bulkAssignee || undefined);
    if (!ok) return;
    toast.success(
      bulkAssignee
        ? `${selection.size} task${selection.size === 1 ? '' : 's'} assigned`
        : `${selection.size} task${selection.size === 1 ? '' : 's'} cleared`,
    );
    setSelection(new Set());
  };

  /* ─── role guards / empty states ─── */
  if (!role) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-950">
        Member assignment is available to <b>department heads</b> and <b>managers</b>. Your role on this
        project doesn't have anyone to delegate to.
      </div>
    );
  }
  if (assignees.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
        <Users className="mx-auto mb-2 h-7 w-7 text-slate-400" />
        <p className="text-sm font-bold text-slate-800">
          {role === 'department_head' ? 'No managers in your department yet' : 'No engineers report to you yet'}
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Add team members on the project Team tab first, then come back to assign tasks.
        </p>
      </div>
    );
  }
  if (scopeIndices.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
        <Target className="mx-auto mb-2 h-7 w-7 text-slate-400" />
        <p className="text-sm font-bold text-slate-800">No tasks in your authority</p>
        <p className="mt-1 text-xs text-slate-500">
          {role === 'department_head'
            ? 'No tasks are routed to your department yet — assign tasks to your dept on the Departments view.'
            : 'No tasks are assigned to you yet — your dept head will route work here once it lands.'}
        </p>
      </div>
    );
  }

  /* ─── render ─── */
  const myDeptName = orgUnitName(orgUnits, mine.orgUnitId);
  const titleNoun = role === 'department_head' ? 'department' : 'team';

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
    >
      {/* ─── Hero header ─── */}
      <div
        className="relative overflow-hidden border-b border-slate-200 px-5 py-4"
        style={{
          background:
            'linear-gradient(135deg, rgba(13,148,136,0.09) 0%, rgba(16,185,129,0.05) 60%, #ffffff 100%)',
        }}
      >
        <div className="relative flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-600 to-emerald-600 text-white shadow-lg shadow-teal-600/30 ring-2 ring-white/80">
              <UserCheck className="h-5 w-5" strokeWidth={2.25} />
            </span>
            <div>
              <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                Assign tasks to your {titleNoun}
              </h2>
              <p className="mt-0.5 text-[12px] leading-snug text-slate-600">
                {role === 'department_head' ? (
                  <>
                    Delegate the <b>{myDeptName || 'department'}</b> tasks to a specific manager. Each
                    task gets one owner.
                  </>
                ) : (
                  <>
                    Re-assign your tasks to the engineers reporting to you. Clearing an assignee keeps
                    the task on you.
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {dirty ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-900 ring-1 ring-amber-200">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
                Unsaved
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-800 ring-1 ring-emerald-200">
                Saved
              </span>
            )}
            <motion.button
              type="button"
              whileTap={{ scale: 0.97 }}
              disabled={saving || !dirty || !canUpdate}
              onClick={() => void onSave()}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 px-4 py-2 text-[12px] font-bold uppercase tracking-wide text-white shadow-lg shadow-teal-600/30 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-45"
              title="Save assignments as a new schedule version"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Saving…' : 'Save version'}
            </motion.button>
          </div>
        </div>

        {/* Stats strip */}
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
          <StatChip label="In scope" value={stats.total} />
          <StatChip label="Assigned" value={stats.assigned} tone="teal" />
          <StatChip label="Unassigned" value={stats.unassigned} tone={stats.unassigned > 0 ? 'amber' : 'slate'} />
          <StatChip label="Stages" value={kinds.stages} />
          <StatChip
            label="Leaf tasks"
            value={`${stats.leafAssigned}/${stats.leafTotal}`}
            tone={stats.leafAssigned === stats.leafTotal ? 'emerald' : 'slate'}
          />
        </div>
      </div>

      {/* ─── Workload pills ─── */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200/80 bg-slate-50/60 px-5 py-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          Team load
        </p>
        {assignees.map((a) => {
          const w = workload.get(a.userId)!;
          const active = filterAssigneeId === a.userId;
          return (
            <button
              key={a.userId}
              type="button"
              onClick={() =>
                setFilterAssigneeId((cur) => (cur === a.userId ? '' : a.userId))
              }
              className={twMerge(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold shadow-sm transition',
                active
                  ? 'border-teal-500 bg-teal-600 text-white'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-teal-300 hover:bg-teal-50/60',
              )}
              title={`${a.userName ?? a.userEmail ?? a.userId} · ${ROLE_LABEL[a.role]}`}
            >
              <User className="h-3 w-3" />
              {a.userName ?? a.userEmail ?? a.userId}
              <span
                className={twMerge(
                  'rounded-full px-1.5 py-[1px] text-[10px] font-bold tabular-nums',
                  active ? 'bg-white/20' : 'bg-slate-100 text-slate-700',
                )}
              >
                {w.taskCount}
              </span>
            </button>
          );
        })}
        {filterAssigneeId ? (
          <button
            type="button"
            onClick={() => setFilterAssigneeId('')}
            className="ml-1 inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1 text-[10.5px] font-semibold text-slate-600 hover:bg-slate-50"
          >
            <X className="h-3 w-3" /> Clear filter
          </button>
        ) : null}
        <div className="ml-auto flex items-center gap-1.5">
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-[11px] font-medium text-slate-600">
            <input
              type="checkbox"
              checked={filterUnassignedOnly}
              onChange={(e) => setFilterUnassignedOnly(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            />
            Unassigned only
          </label>
        </div>
      </div>

      {/* ─── Bulk-action bar ─── */}
      {canUpdate ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200/80 bg-white px-5 py-3">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-700">
            <Check className="h-3.5 w-3.5" />
            {selection.size} selected
          </span>
          <select
            value={bulkAssignee}
            onChange={(e) => setBulkAssignee(e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[12px] font-medium text-slate-700 shadow-sm"
          >
            <option value="">— unassign —</option>
            {assignees.map((a) => (
              <option key={a.userId} value={a.userId}>
                {a.userName ?? a.userEmail ?? a.userId}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={selection.size === 0}
            onClick={applyBulk}
            className="inline-flex items-center gap-1.5 rounded-md bg-slate-800 px-3 py-1.5 text-[11.5px] font-bold uppercase tracking-wide text-white shadow-sm hover:bg-slate-900 disabled:opacity-40"
          >
            <Zap className="h-3.5 w-3.5" />
            Apply
          </button>
          <button
            type="button"
            disabled={selection.size === 0}
            onClick={clearSelection}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[11.5px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </button>

          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={expandAll}
              className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={collapseAll}
              className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
            >
              Collapse
            </button>
          </div>
        </div>
      ) : null}

      {/* ─── Task tree ─── */}
      <div className="max-h-[64vh] overflow-auto px-3 py-3">
        <ul className="space-y-1">
          {tree.map((n) => (
            <TreeRow
              key={n.rowIndex}
              node={n}
              depth={0}
              tasks={scopedTasks}
              originalTasks={tasks}
              scopedToOriginal={scopedToOriginal}
              expanded={expanded}
              selection={selection}
              onToggleExpand={toggleExpand}
              onToggleSelect={toggleSelection}
              onSelectSubtree={selectSubtree}
              onSetRowAssignee={(scopedIdx, uid) => setAssignee([scopedIdx], uid)}
              memberships={memberships}
              assignees={assignees}
              canUpdate={canUpdate}
              filterUnassignedOnly={filterUnassignedOnly}
              filterAssigneeId={filterAssigneeId}
              isLeafScoped={(idx) => leafScopedIndices.has(idx)}
            />
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

/* ─────────────────────────── TREE ROW ─────────────────────────── */

function TreeRow({
  node,
  depth,
  tasks,
  originalTasks,
  scopedToOriginal,
  expanded,
  selection,
  onToggleExpand,
  onToggleSelect,
  onSelectSubtree,
  onSetRowAssignee,
  memberships,
  assignees,
  canUpdate,
  filterUnassignedOnly,
  filterAssigneeId,
  isLeafScoped,
}: {
  node: EpcWbsNode;
  depth: number;
  /** Scoped subset (for the picker UI). */
  tasks: EpcScheduleTaskRow[];
  /** Original tasks (for accurate assignee lookup). */
  originalTasks: EpcScheduleTaskRow[];
  scopedToOriginal: number[];
  expanded: Set<number>;
  selection: Set<number>;
  onToggleExpand: (rowIndex: number) => void;
  onToggleSelect: (scopedIdx: number) => void;
  onSelectSubtree: (node: EpcWbsNode) => void;
  onSetRowAssignee: (scopedIdx: number, uid: string | undefined) => void;
  memberships: EpcProjectMembershipRow[];
  assignees: EpcProjectMembershipRow[];
  canUpdate: boolean;
  filterUnassignedOnly: boolean;
  filterAssigneeId: string;
  isLeafScoped: (scopedIdx: number) => boolean;
}) {
  const t = tasks[node.rowIndex]!;
  const origIdx = scopedToOriginal[node.rowIndex]!;
  const origTask = originalTasks[origIdx]!;
  const assignedUid = taskAssignedUserId(origTask);
  const isOpen = node.children.length === 0 || expanded.has(node.rowIndex);
  const isLeaf = isLeafScoped(node.rowIndex);

  /* Filtering only applies to leaf rows (parent rows always show so the path is reachable). */
  let filteredOut = false;
  if (isLeaf) {
    if (filterUnassignedOnly && assignedUid) filteredOut = true;
    if (filterAssigneeId && assignedUid !== filterAssigneeId) filteredOut = true;
  }

  /* If a parent has no descendant matching the filter, hide it. */
  const hasMatchingDescendant = useMemoChildrenMatch(
    node,
    tasks,
    originalTasks,
    scopedToOriginal,
    filterUnassignedOnly,
    filterAssigneeId,
    isLeafScoped,
  );

  if (filteredOut) return null;
  if (!isLeaf && !hasMatchingDescendant) return null;

  const padLeft = depth * 16;
  const isSelected = selection.has(node.rowIndex);
  const taskName = t.name?.toString().trim() || `Row ${node.rowIndex + 1}`;
  const wbs = t.wbs?.toString().trim() || '';
  const assignedRow = assignedUid
    ? memberships.find((m) => m.userId === assignedUid)
    : undefined;
  const assignedLabel = assignedRow
    ? assignedRow.userName ?? assignedRow.userEmail ?? assignedUid
    : '';

  return (
    <li>
      <div
        className={twMerge(
          'group flex items-center gap-2 rounded-lg border px-2.5 py-1.5 transition-colors',
          kindRailClasses(node.kind),
          isSelected
            ? 'border-slate-400 bg-slate-100 ring-1 ring-slate-300'
            : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/70',
        )}
        style={{ paddingLeft: 10 + padLeft }}
      >
        {/* expand/collapse */}
        {node.children.length > 0 ? (
          <button
            type="button"
            onClick={() => onToggleExpand(node.rowIndex)}
            className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            {expanded.has(node.rowIndex) ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <span className="w-[18px]" aria-hidden />
        )}

        {/* selection checkbox */}
        {canUpdate ? (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(node.rowIndex)}
            className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            title="Select for bulk assign"
          />
        ) : null}

        {/* kind badge */}
        <span
          className={twMerge(
            'rounded px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-wider',
            kindBadgeClasses(node.kind),
          )}
        >
          {node.kind === 'stage' ? 'Stage' : node.kind === 'task' ? 'Task' : 'Sub'}
        </span>

        {/* WBS code */}
        {wbs ? (
          <span className="font-mono text-[10.5px] text-slate-500 tabular-nums">{wbs}</span>
        ) : null}

        {/* name */}
        <span
          className="min-w-0 flex-1 truncate text-[12px] font-medium text-slate-800"
          title={taskName}
        >
          {taskName}
        </span>

        {/* current assignee chip / picker */}
        <div className="flex shrink-0 items-center gap-1.5">
          {assignedRow ? (
            <span className="inline-flex items-center gap-1 rounded-md bg-teal-50 px-1.5 py-0.5 text-[10px] font-semibold text-teal-800 ring-1 ring-teal-200">
              <CheckCircle2 className="h-3 w-3" />
              <span className="max-w-[140px] truncate">{assignedLabel}</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 ring-1 ring-amber-200">
              <CircleDashed className="h-3 w-3" />
              Unassigned
            </span>
          )}
          {canUpdate ? (
            <select
              value={assignedUid ?? ''}
              onChange={(e) =>
                onSetRowAssignee(node.rowIndex, e.target.value || undefined)
              }
              className="rounded-md border border-slate-200 bg-white px-1.5 py-1 text-[11px] font-medium text-slate-700 shadow-sm"
              title="Set assignee"
            >
              <option value="">— unassigned —</option>
              {assignees.map((a) => (
                <option key={a.userId} value={a.userId}>
                  {a.userName ?? a.userEmail ?? a.userId}
                </option>
              ))}
            </select>
          ) : null}
          {canUpdate && node.children.length > 0 ? (
            <button
              type="button"
              onClick={() => onSelectSubtree(node)}
              title="Select this and everything inside it"
              className="rounded-md border border-slate-200 bg-white p-1 text-slate-500 shadow-sm hover:bg-slate-50 hover:text-slate-700"
            >
              <Building2 className="h-3 w-3" />
            </button>
          ) : null}
        </div>
      </div>

      {isOpen && node.children.length > 0 ? (
        <ul className="mt-1 space-y-1">
          {node.children.map((c) => (
            <TreeRow
              key={c.rowIndex}
              node={c}
              depth={depth + 1}
              tasks={tasks}
              originalTasks={originalTasks}
              scopedToOriginal={scopedToOriginal}
              expanded={expanded}
              selection={selection}
              onToggleExpand={onToggleExpand}
              onToggleSelect={onToggleSelect}
              onSelectSubtree={onSelectSubtree}
              onSetRowAssignee={onSetRowAssignee}
              memberships={memberships}
              assignees={assignees}
              canUpdate={canUpdate}
              filterUnassignedOnly={filterUnassignedOnly}
              filterAssigneeId={filterAssigneeId}
              isLeafScoped={isLeafScoped}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/** Memo-friendly check: does any descendant of `node` (or `node` itself if leaf) pass the active filter? */
function useMemoChildrenMatch(
  node: EpcWbsNode,
  tasks: EpcScheduleTaskRow[],
  originalTasks: EpcScheduleTaskRow[],
  scopedToOriginal: number[],
  filterUnassignedOnly: boolean,
  filterAssigneeId: string,
  isLeafScoped: (scopedIdx: number) => boolean,
): boolean {
  return useMemo(() => {
    if (!filterUnassignedOnly && !filterAssigneeId) return true;
    let match = false;
    const walk = (n: EpcWbsNode) => {
      if (match) return;
      if (n.children.length === 0 && isLeafScoped(n.rowIndex)) {
        const orig = scopedToOriginal[n.rowIndex];
        if (orig == null) return;
        const ot = originalTasks[orig];
        if (!ot) return;
        const a = taskAssignedUserId(ot);
        if (filterUnassignedOnly && a) return;
        if (filterAssigneeId && a !== filterAssigneeId) return;
        match = true;
        return;
      }
      for (const c of n.children) walk(c);
    };
    walk(node);
    return match;
  }, [
    node,
    tasks,
    originalTasks,
    scopedToOriginal,
    filterUnassignedOnly,
    filterAssigneeId,
    isLeafScoped,
  ]);
}
